import { useEffect, useState } from 'react';
import { CloseButton, Modal } from 'react-bootstrap';

import BenchmarkCreationGoalDisplay from '../../components/BenchmarkCreationGoals/BenchmarkCreationGoalDisplay';
import BenchmarkCreationGoalsTable from '../../components/BenchmarkCreationGoals/BenchmarkCreationGoalsTable';
import LoadingWrapper from '../../components/LoadingWrapper';
import ProjectDropdown from '../../components/ProjectDropdown';
import ErrorMessage from '../../components/ToastMessages/ErrorMessages';
import { errorToast, infoToast, MessageOptions, successToast } from '../../components/Toasty';

import {
    userCanAddBenchmarkCreationGoal,
    userCanEditBenchmarkCreationGoal,
} from '../../functions/AccessHelpers'
import { stripToYmdStrict } from '../../functions/TimeHelpers';

import {
    GetTierIdFromActivityName,
} from '../../functions/BenchmarkGoalCreationHelpers';

import { getActivities } from '../../services/Quality/BenchmarkActivities';
import {
    createBenchmarkGoal,
    deleteBenchmarkGoal,
    listBenchmarkGoals,
    updateBenchmarkGoal,
} from '../../services/Quality/BenchmarkGoals';

import EmployeeInfo from '../../types/Employee/EmployeeInfo'
import { Activity } from '../../types/Quality/Activity';
import { ActivityTrade } from '../../types/Quality/ActivityTrade';
import { BenchmarkGoal, CreateBenchmarkGoalRequest, UpdateBenchmarkGoalRequest } from '../../types/Quality/Benchmark';
import { Trade } from '../../types/Quality/Trade';
import Project from '../../types/Dashboard/Project';

// Default goal used to reset modal state and avoid undefined checks
const EMPTY_GOAL: BenchmarkGoal = {
    id: 0,
    activityTradeId: 0,
    activity: {
        id: 0,
        name: '',
        nameNormalized: '',
        isActive: true,
        trades: [],
        isLocked: false,
        aliases: [],
        updatedAt: '',
        updatedBy: '',
        rowVersion: null,
    },
    trade: {
        id: 0,
        name: '',
        isActive: true,
    },
    tier: {
        formID: '',
        formName: '',
        trade: '',
    },
    targetCompletionDate: '',
    createUser: {
        employeeJobCode: '',
        employeeLocation: '',
        employeeID: '',
        employeeName: '',
        employeeEmail: '',
        manualAdd: true,
    },
    createDate: '',
    modifiedUser: {
        employeeJobCode: '',
        employeeLocation: '',
        employeeID: '',
        employeeName: '',
        employeeEmail: '',
        manualAdd: true,
    },
    modifiedDate: '',
}

// .NET serializes an unset TargetCompletion (DateTime.MinValue) as 0001-01-01.
// Rows completed via Forms On Fire before an admin generated the goal carry this default.
const DEFAULT_TARGET_DATE = '0001-01-01';

const isDefaultTargetDate = (value?: string | null) => {
    const ymd = (value ?? '').trim();
    return ymd.length === 0 || ymd === DEFAULT_TARGET_DATE;
};

interface BenchmarkCreationGoalProps {
    userData: EmployeeInfo,
    isMocked: boolean,
    projects: Project[];
    cookieName: string;
};

// Toast message options for error handling
const BenchmarkErrorOptions: MessageOptions = {
    autoClose: false,
    pauseOnHover: true,
    customID: undefined,
    closeOnClick: false,
};

const BenchmarkCreationGoalPage = ({
    userData,
    isMocked,
    projects,
    cookieName,
}: BenchmarkCreationGoalProps) => {
    // Local UI state for modal visibility, permissions, loading, and data sets
    const [selectedProject, setSelectedProject] = useState<string | undefined>(undefined);
    const [showAddGoalModal, setShowAddGoalModal] = useState<boolean>(false);

    const [userCanAdd, setUserCanAdd] = useState<boolean>(false);
    const [userCanEdit, setUserCanEdit] = useState<boolean>(false);

    const [goalsLoading, setGoalsLoading] = useState<boolean>(false);
    const [goalsLoadingMessage, setGoalsLoadingMessage] = useState<string>('');

    const [benchmarkGoalList, setBenchmarkGoalList] = useState<BenchmarkGoal[]>([]);
    const [activityList, setActivityList] = useState<Activity[]>([]);

    const [targetbenchmark, setTargetBenchmark] = useState<BenchmarkGoal>(EMPTY_GOAL);
    const [editedbenchmark, setEditedBenchmark] = useState<BenchmarkGoal>(EMPTY_GOAL);
    const [availableActivityTrades, setAvailableActivityTrades] = useState<ActivityTrade[]>([]);
    const [isEditingGoal, setIsEditingGoal] = useState<boolean>(false);
    const [isDeletingGoal, setIsDeletingGoal] = useState<boolean>(false);
    const [isGoalActionEnabled, setIsGoalActionEnabled] = useState<boolean>(false);
    // Track the trade/activity names when a user attempts to add a duplicate combo
    const [duplicateGoalInfo, setDuplicateGoalInfo] = useState<{ trade: string; activity: string } | null>(null);

    // Keep edited benchmark in sync when the target selection changes
    useEffect(() => {
        setEditedBenchmark(targetbenchmark);
    }, [targetbenchmark]);

    // Update permissions when the logged-in user changes
    useEffect(() => {
        setUserCanAdd(userCanAddBenchmarkCreationGoal(userData));
        setUserCanEdit(userCanEditBenchmarkCreationGoal(userData));
    }, [userData]);

    /**
     * Simple helper to check whether a goal field has content.
     * Keeps null/undefined/whitespace handling consistent across validations.
     */
    const goalFieldFilled = (value?: string | null) => (value ?? '').trim().length > 0;

    /**
     * Wraps error details in a reusable toast-friendly JSX block so we keep
     * a consistent look/feel for error handling throughout the page.
     */
    const buildErrorMessage = (title: string, description: string, error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        return (
            <ErrorMessage title={title} error={errorMessage}>
                <p>{description}</p>
            </ErrorMessage>
        );
    };

    // Enable/disable modal action button based on edit mode and field validity
    useEffect(() => {
        if (!isEditingGoal) {
            const hasTrade = goalFieldFilled(editedbenchmark.trade.name);
            const hasActivity = goalFieldFilled(editedbenchmark.activity.name);
            const hasTargetCompletion = goalFieldFilled(editedbenchmark.targetCompletionDate);
            setIsGoalActionEnabled(hasTrade && hasActivity && hasTargetCompletion);
            return;
        }

        const originalTarget = (targetbenchmark.targetCompletionDate ?? '').trim();
        const updatedTarget = (editedbenchmark.targetCompletionDate ?? '').trim();
        const hasChangedTarget = updatedTarget.length > 0 && updatedTarget !== originalTarget;
        setIsGoalActionEnabled(hasChangedTarget);
    }, [editedbenchmark, targetbenchmark, isEditingGoal]);

    // Load activities and benchmark goals whenever the selected project changes
    useEffect(() => {
        let cancelled = false;

        /**
         * Pulls activity data (and builds a combined activity/trade list),
         * then fetches benchmark goals for the selected project. Keeps local
         * state synchronized with backend data and normalizes dates for UI use.
         */
        const loadProjectGoalDetails = async () => {
            const projectId = selectedProject;
            if (!projectId) {
                setBenchmarkGoalList([]);
                return;
            }

            try {
                setGoalsLoading(true);
                if (activityList.length === 0) {
                    setGoalsLoadingMessage('Loading Activity Data');
                    const [activityListResponse] = await Promise.all([getActivities()]);
                    if (cancelled) return;
                    setActivityList(activityListResponse.data.items);

                    // Create available benchmarks from activities with trades (one benchmark per trade)
                    const activitiesWithTrades: ActivityTrade[] = activityListResponse.data.items
                        .filter((activity: Activity) => activity.isActive && activity.trades.length > 0)
                        .flatMap((activity: Activity) =>
                            activity.trades
                                .filter((trade: Trade) => trade.isActive)
                                .map((trade: Trade) => ({
                                    activityID: activity.id,
                                    activityName: activity.name,
                                    tradeName: trade.name,
                                    tradeID: trade.id,
                                    activityIsActive: activity.isActive,
                                    tradeIsActive: trade.isActive,
                                }))
                        );

                    // Add activities with no trades
                    const activitiesWithoutTrades: ActivityTrade[] = activityListResponse.data.items
                        .filter((activity: Activity) => activity.isActive && activity.trades.length === 0)
                        .map((activity: Activity) => ({
                            activityID: activity.id,
                            activityName: activity.name,
                            tradeID: 0,
                            tradeName: '',
                            activityIsActive: activity.isActive,
                            tradeIsActive: false,
                        }));

                    // Combine both arrays and set IDs
                    const activities: ActivityTrade[] = [...activitiesWithTrades, ...activitiesWithoutTrades]
                    setAvailableActivityTrades(activities);
                }

                setGoalsLoadingMessage('Loading Benchmark Goals Creation Data');
                const { goals } = await listBenchmarkGoals({ companyId: projectId });
                if (cancelled) return;

                const mappedBenchmarkGoals: BenchmarkGoal[] = goals?.filter(g => g.activityTradeId !== 0)
                    .map(({ targetCompletionDate, ...goal }) => ({
                        ...goal,
                        targetCompletionDate: stripToYmdStrict(targetCompletionDate),
                    })) ?? [];

                setBenchmarkGoalList(mappedBenchmarkGoals);

            } catch (error) {
                if (cancelled) return;
                const errorMessage = error instanceof Error ? error.message : String(error);
                const msg = (
                    <ErrorMessage
                        title='Error Retrieving Data'
                        error={errorMessage}
                    >
                        <p>
                            {'Retrieving Activity Data Failed'}
                        </p>
                    </ErrorMessage>
                );
                errorToast(msg, BenchmarkErrorOptions);
                console.error(error);
            } finally {
                if (!cancelled) {
                    setGoalsLoadingMessage('');
                    setGoalsLoading(false);
                }
            }
        };

        loadProjectGoalDetails();

        return () => { cancelled = true; };
    }, [selectedProject, activityList.length]);

    /**
     * Create a new benchmark goal via API, update the table, and reset modal state.
     * Guarded for mock mode and missing project selections.
     */
    const handleAddGoal = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Add" button.  This has been disabled because you are currently not yourself.')
            return;
        }

        if (!selectedProject) {
            errorToast('No project selected.  Cannot add benchmark goal.', BenchmarkErrorOptions);
            return;
        }

        const activityId = editedbenchmark.activity?.id ?? 0;
        const tradeId = editedbenchmark.trade?.id ?? 0;

        // Ensure each activity/trade pair only appears once per project.
        // A matching row whose target date is still the default was completed via
        // FOF before a goal was generated, so it doesn't block creation — the API
        // updates that row instead of inserting a duplicate.
        const duplicateGoalExists = benchmarkGoalList.some((goal) =>
            (goal.activity?.id ?? 0) === activityId &&
            (goal.trade?.id ?? 0) === tradeId &&
            !isDefaultTargetDate(goal.targetCompletionDate)
        );

        if (duplicateGoalExists) {
            const tradeName = editedbenchmark.trade?.name || 'N/A';
            const activityName = editedbenchmark.activity?.name || 'N/A';
            const warningMessage = `Benchmark goal already exists for activity "${activityName}" and trade "${tradeName}".`;
            console.warn(warningMessage);
            // Store the duplicate names so the modal can inform the user
            setDuplicateGoalInfo({ trade: tradeName, activity: activityName });
            return;
        }

        const createBenchamrkRequest: CreateBenchmarkGoalRequest = {
            companyId: selectedProject,
            activityId: editedbenchmark.activity.id,
            tradeId: editedbenchmark.trade.id,
            tier: GetTierIdFromActivityName(editedbenchmark.activity.name),
            targetCompletionDate: editedbenchmark.targetCompletionDate,
            userEmail: userData.email,
        }


        try {
            const { data } = await createBenchmarkGoal(createBenchamrkRequest);

            if (!data.benchmarkGoal) {
                throw new Error('Server did not return benchmark goal data.');
            }

            const normalizedGoal: BenchmarkGoal = {
                ...data.benchmarkGoal,
                targetCompletionDate: stripToYmdStrict(data.benchmarkGoal.targetCompletionDate),
            };

            // The API updates a pre-completed row in place (same id) rather than
            // inserting, so replace the existing entry when the id is already listed.
            setBenchmarkGoalList((current) =>
                current.some((goal) => goal.id === normalizedGoal.id)
                    ? current.map((goal) => (goal.id === normalizedGoal.id ? normalizedGoal : goal))
                    : [...current, normalizedGoal]
            );
            setTargetBenchmark(EMPTY_GOAL);
            setEditedBenchmark(EMPTY_GOAL);
            setIsEditingGoal(false);
            setIsDeletingGoal(false);
            setShowAddGoalModal(false);
            successToast('Benchmark goal added successfully.');
        } catch (error) {
            console.error('Error creating benchmark goal:', error);
            const msg = buildErrorMessage(
                'Error Adding Benchmark Goal',
                'Unable to add the benchmark goal. Please try again.',
                error
            );
            errorToast(msg, BenchmarkErrorOptions);
        }
    };

    /**
     * Persist changes to an existing goal (currently only target date),
     * then refresh local list and close the modal.
     */
    const handlUpdateGoal = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Update" button.  This has been disabled because you are currently not yourself.')
            return;
        }

        const updateBenchmarkRequest: UpdateBenchmarkGoalRequest = {
            benchmarkId: editedbenchmark.id,
            targetCompletionDate: editedbenchmark.targetCompletionDate,
            userEmail: userData.email,
        }

        try {
            const { data } = await updateBenchmarkGoal(updateBenchmarkRequest);

            if (!data.benchmarkGoal) {
                throw new Error('Server did not return benchmark goal data.');
            }

            const normalizedGoal: BenchmarkGoal = {
                ...data.benchmarkGoal,
                targetCompletionDate: stripToYmdStrict(data.benchmarkGoal.targetCompletionDate),
            };

            setBenchmarkGoalList((current) =>
                current.map((goal) =>
                    goal.id === normalizedGoal.id ? normalizedGoal : goal
                )
            );

            setTargetBenchmark(EMPTY_GOAL);
            setEditedBenchmark(EMPTY_GOAL);
            setIsEditingGoal(false);
            setIsDeletingGoal(false);
            setShowAddGoalModal(false);
            successToast('Benchmark goal updated successfully.');

        } catch (error) {
            console.error('Error updating benchmark goal:', error);
            const msg = buildErrorMessage(
                'Error Updating Benchmark Goal',
                'Unable to update the benchmark goal. Please try again.',
                error
            );
            errorToast(msg, BenchmarkErrorOptions);
        }
    };

    /**
     * Initialize modal state for creating a new goal, clearing any prior selection
     * and ensuring edit/delete flags are reset.
     */
    const handleAddClick = () => {
        setIsEditingGoal(false);
        setIsDeletingGoal(false);
        setTargetBenchmark(EMPTY_GOAL);
        setEditedBenchmark(EMPTY_GOAL);
        setShowAddGoalModal(true);
    };

    /**
     * Open modal in edit mode for a specific goal id. If the goal is missing,
     * we log a warning and skip opening the modal.
     */
    const handleGoalClick = (id: number) => {
        const selectedGoal = benchmarkGoalList.find((goal) => goal.id === id);
        if (!selectedGoal) {
            console.warn(`Benchmark goal with id ${id} not found.`);
            return;
        }

        setIsEditingGoal(true);
        setIsDeletingGoal(false);
        setTargetBenchmark(selectedGoal);
        setShowAddGoalModal(true);
    };

    // Prepare modal state for confirming a delete action
    const handleDeleteClick = async (id: number) => {
        setIsDeletingGoal(true);
        setIsEditingGoal(false);
        setTargetBenchmark(benchmarkGoalList.find((goal) => goal.id === id) || EMPTY_GOAL);
        setShowAddGoalModal(true);
    }

    /**
     * Execute server-side delete and update local list, then reset modal state.
     * No-ops when in mock mode.
     */
    const handleDeleteGoal = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Delete" button.  This has been disabled because you are currently not yourself.')
            return;
        }

        try {
            await deleteBenchmarkGoal({ benchmarkId: targetbenchmark.id, userEmail: userData.email });

            setBenchmarkGoalList((current) =>
                current.filter((goal) => goal.id !== targetbenchmark.id)
            );

            setTargetBenchmark(EMPTY_GOAL);
            setEditedBenchmark(EMPTY_GOAL);
            setIsEditingGoal(false);
            setIsDeletingGoal(false);
            setShowAddGoalModal(false);
            successToast('Benchmark goal deleted successfully.');
        } catch (error) {
            console.error('Error deleting benchmark goal:', error);
            const msg = buildErrorMessage(
                'Error Deleting Benchmark Goal',
                'Unable to delete the benchmark goal. Please try again.',
                error
            );
            errorToast(msg, BenchmarkErrorOptions);
        }
    };

    /**
     * Modal wrapper used for all goal flows (add, edit, delete). Button labeling
     * and handlers are chosen based on the current mode flags.
     */
    const closeDuplicateGoalModal = () => setDuplicateGoalInfo(null);

    const AddGoalModal = () => (
        <Modal
            show={showAddGoalModal}
            keyboard={false}
            size='lg'
            enforceFocus={false}
        >
            <Modal.Header>
                <div className='col'>
                    <div className='row'>
                        <div className='col-1'></div>
                        <div className='col-10 h3 text-center'>
                            {isEditingGoal ? 'Update Creation Goal' : isDeletingGoal ? 'Delete Creation Goal' : 'Add Creation Goal'}
                        </div>
                        <div className='col-1'>
                            <CloseButton
                                onClick={() => setShowAddGoalModal(false)}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <BenchmarkCreationGoalDisplay
                    goal={targetbenchmark}
                    availableActivityTrades={availableActivityTrades}
                    canEdit={userCanEdit || userCanAdd}
                    onEdit={(g: BenchmarkGoal) => setEditedBenchmark(g)}
                    isEditing={isEditingGoal}
                    isDeleting={isDeletingGoal}
                />
            </Modal.Body>
            <Modal.Footer>
                <div className='col'>
                    <div className='row'>
                        <div className='col-2'></div>
                        <div className='col-8 text-center'>
                            <button
                                className='btn btn-primary'
                                onClick={isEditingGoal ? handlUpdateGoal : isDeletingGoal ? handleDeleteGoal : handleAddGoal}
                                disabled={!isGoalActionEnabled}
                            >
                                {isEditingGoal ? 'Update' : isDeletingGoal ? 'Delete' : 'Add'}
                            </button>
                        </div>
                        <div className='col-2'>
                            <button
                                className='btn btn-danger'
                                onClick={() => setShowAddGoalModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );

    // Simple modal explaining that the chosen trade/activity already exists
    const DuplicateGoalModal = () => (
        <Modal
            show={!!duplicateGoalInfo}
            onHide={closeDuplicateGoalModal}
            centered
        >
            <Modal.Header closeButton>
                <div className='h5 mb-0'>Goal Already Exists</div>
            </Modal.Header>
            <Modal.Body>
                <p>A benchmark goal already exists for the selected combination.</p>
                <ul>
                    <li><b>Trade:</b> {duplicateGoalInfo?.trade || 'Unknown Trade'}</li>
                    <li><b>Activity:</b> {duplicateGoalInfo?.activity || 'Unknown Activity'}</li>
                </ul>
                <p className='mb-0'>Please edit the existing goal instead of creating a duplicate.</p>
            </Modal.Body>
            <Modal.Footer>
                <button className='btn btn-primary' onClick={closeDuplicateGoalModal}>OK</button>
            </Modal.Footer>
        </Modal>
    );

    // Page shell with project selector, action button, and goals table
    return (
        <div>
            {AddGoalModal()}
            {DuplicateGoalModal()}
            <div className='row' >
                <ProjectDropdown
                  projects={projects}
                  cookieName={cookieName}
                  onProjectChange={(project) => {
                    setSelectedProject(project)
                  }}
                />
            </div>
            <div className='row border-bottom mx-3'>
                <div className='col-3'>
                </div>
                <div className='col-6 text-center h2 my-3'>
                    Benchmark Creation Goals
                </div>
                <div className='col-2 my-3'>
                </div>
                <div className='col-1 my-3'>
                    {
                        userCanAdd &&
                        <button
                            title='Add a New Benchmark Creation Goal'
                            className='btn btn-primary'
                            onClick={handleAddClick}
                            disabled={!selectedProject}
                        >
                            Add
                        </button>
                    }
                </div>
            </div>
            <div className='row pt-1 mt-1'>
                <div className='col'>
                    <LoadingWrapper
                        showLoading={goalsLoading}
                        loadingMessage={goalsLoadingMessage}
                    >
                        <BenchmarkCreationGoalsTable
                            goalList={benchmarkGoalList}
                            onGoalClick={handleGoalClick}
                            onDeleteClick={handleDeleteClick}
                            canEdit={userCanEdit}
                        />
                    </LoadingWrapper>
                </div>
            </div>
        </div>
    );
};

export default BenchmarkCreationGoalPage;
